import { PerformanceOvertimeReport } from '@/client/reports';
import { Subjects } from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FlexContainer } from '@/ui/styled-ui';
import {
  calculateExtendedTrendLine,
  getTimeBasedLabels,
} from '@/utils/helpers';
import {
  colorBlackMain,
  colorGrayDark,
  colorGrayPale,
  colorGreenChartDark,
  colorGreenChartLight,
  colorGreenChartMiddle,
  platformFontFamily,
} from '@/utils/helpers/css-variables.helper';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  Title,
  Tooltip,
} from 'chart.js';
import { Chart } from 'primereact/chart';
import { Skeleton } from 'primereact/skeleton';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  createCustomTooltip,
  hideTooltipForNavigation,
} from '../charts.helper';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const ChartWrapper = styled.div`
  height: 100%;
  width: 100%;
  min-width: 800px;
`;

const ChartContainer = styled.div`
  overflow-x: scroll;
  width: 100%;
  height: 510px;
  min-height: 510px;
  position: relative;
  border: 1px solid var(--gray-medium);
  border-radius: var(--xsmall-border-radius);
  padding: var(--big-padding) var(--default-padding) var(--default-padding);
  background: var(--white-main);
  margin-top: var(--default-padding);
`;

const StyledBarChart = styled(Chart)`
  width: 100%;
  height: 100%;
`;

type PhishingBarChartProps = {
  fromDate: Date | undefined;
  toDate: Date | undefined;
  id?: string;
  reportData?: PerformanceOvertimeReport[];
  isLoading?: boolean;
  isMainDashboard?: boolean;
};

const getDataMappedToLabels = (
  reportData: PerformanceOvertimeReport[] | undefined,
  labels: { [key: number]: string }[],
  isYearly?: boolean,
): {
  label: string;
  value: PerformanceOvertimeReport | null;
}[] => {
  if (!reportData || !labels) {
    return [];
  }

  if (isYearly) {
    return labels.map((labelObject: { [key: number]: string }) => {
      const matchingItem = reportData?.find((item) => {
        return Object.values(labelObject).includes(item.period);
      });
      return {
        label: Object.values(labelObject)[0],
        value: matchingItem || null,
      };
    });
  } else {
    return labels.map((labelObject: { [key: number]: string }) => {
      const matchingItem = reportData?.find((item) => {
        const month = Number(item?.period?.split('-')[0]) - 1;
        return month === Number(Object.keys(labelObject)[0]);
      });
      return {
        label: Object.values(labelObject)[0],
        value: matchingItem || null,
      };
    });
  }
};

export const PhishingBarChart = ({
  fromDate,
  toDate,
  id,
  reportData,
  isLoading,
  isMainDashboard = false,
}: PhishingBarChartProps) => {
  const { t } = useTranslation();
  const { canUseFeature } = useFeatureFlag();
  const navigate = useNavigate();
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const [datasets, setDatasets] = useState<
    {
      label: string;
      value: PerformanceOvertimeReport | null;
    }[]
  >([]);
  const [isChartDataLoading, setIsChartDataLoading] = useState(false);
  const [backgroundPlugin, setBackgroundPlugin] = useState<any>(null);
  const docStyle = getComputedStyle(document.documentElement);
  const textColor = colorGrayDark(docStyle);
  const textColorSecondary = colorBlackMain(docStyle);
  const hasReportingEnabled = canUseFeature(
    Subjects.OUTLOOK_PHISHING_EMAIL_REPORTING,
  );
  const [datasetVisibility, setDatasetVisibility] = useState({
    background: true,
    reported: hasReportingEnabled,
    clicked: true,
    submitted: true,
    reportedTrend: hasReportingEnabled,
    clickedTrend: true,
    submittedTrend: true,
  });

  const handleLegendClick = (datasetKey: string) => {
    setDatasetVisibility((prev) => ({
      ...prev,
      [datasetKey]: !prev[datasetKey as keyof typeof prev],
    }));
  };

  const performanceOverTimeLabels = getTimeBasedLabels(
    fromDate,
    toDate,
    reportData,
    t,
  );

  const hasNoData =
    !reportData || reportData.length === 0 || datasets.length === 0;

  useEffect(() => {
    if (!reportData || !performanceOverTimeLabels) return;
    const datasetsData = getDataMappedToLabels(
      reportData,
      performanceOverTimeLabels,
      !fromDate && !toDate,
    );
    setIsChartDataLoading(true);
    setDatasets(datasetsData);
  }, [reportData]);

  useEffect(() => {
    return () => {
      const tooltipId = `chart-tooltip-${id}-line-chart`;
      hideTooltipForNavigation(tooltipId);
    };
  }, [id]);

  useEffect(() => {
    const reportRateData = hasReportingEnabled
      ? datasets?.map((item: any) => {
          return item.value?.reportedRate
            ? Math.min(Number(item.value.reportedRate), 100)
            : null;
        }) || []
      : [];

    const clickRateData =
      datasets?.map((item: any) => {
        return item.value?.clickedRate
          ? Math.min(Number(item.value.clickedRate), 100)
          : null;
      }) || [];

    const submissionRateData =
      datasets?.map((item: any) => {
        return item.value?.submittedRate
          ? Math.min(Number(item.value.submittedRate), 100)
          : null;
      }) || [];

    const backgroundData =
      datasets?.map((item: any) => {
        const hasReportedData =
          item.value?.reportedRate !== null &&
          item.value?.reportedRate !== undefined;
        const hasClickedData =
          item.value?.clickedRate !== null &&
          item.value?.clickedRate !== undefined;
        const hasSubmittedData =
          item.value?.submittedRate !== null &&
          item.value?.submittedRate !== undefined;
        const hasSentEmails =
          item.value?.sentCount !== null && item.value?.sentCount !== undefined;

        return hasReportedData ||
          hasClickedData ||
          hasSubmittedData ||
          hasSentEmails ||
          hasSentEmails
          ? 100
          : null;
      }) || [];

    const allValues = [
      ...(hasReportingEnabled
        ? reportRateData.filter((val): val is number => val !== null)
        : []),
      ...clickRateData.filter((val): val is number => val !== null),
      ...submissionRateData.filter((val): val is number => val !== null),
    ];

    const maxDataValue = allValues.length > 0 ? Math.max(...allValues) : 0;
    const roundedMax = Math.min(Math.ceil(maxDataValue / 10) * 10, 100);

    const reportRateTrend = hasReportingEnabled
      ? calculateExtendedTrendLine(reportRateData)
      : [];
    const clickRateTrend = calculateExtendedTrendLine(clickRateData);
    const submissionRateTrend = calculateExtendedTrendLine(submissionRateData);

    const chartDatasets = [
      {
        type: 'bar',
        label: t('phishing.chart.clickRate'),
        backgroundColor: colorGreenChartLight(docStyle),
        data: datasets?.map((item: any) =>
          item.value?.clickedRate
            ? parseFloat(
                Math.min(Number(item.value?.clickedRate || 0), 100).toFixed(2),
              )
            : null,
        ),
        hoverBackgroundColor: `color-mix(in srgb, ${colorGreenChartLight(
          docStyle,
        )}, black 20%)`,
        hidden: !datasetVisibility.clicked,
      },
      {
        type: 'bar',
        label: t('phishing.chart.submissionRate'),
        backgroundColor: colorGreenChartMiddle(docStyle),
        data: datasets?.map((item: any) =>
          item.value?.submittedRate
            ? parseFloat(
                Math.min(Number(item.value?.submittedRate || 0), 100).toFixed(
                  2,
                ),
              )
            : null,
        ),
        hoverBackgroundColor: `color-mix(in srgb, ${colorGreenChartMiddle(
          docStyle,
        )}, black 20%)`,
        hidden: !datasetVisibility.submitted,
      },
      ...(hasReportingEnabled
        ? [
            {
              type: 'bar',
              label: t('phishing.chart.reportRate'),
              backgroundColor: colorGreenChartDark(docStyle),
              data: datasets?.map((item: any) =>
                item.value?.reportedRate
                  ? parseFloat(
                      Math.min(
                        Number(item.value?.reportedRate || 0),
                        100,
                      ).toFixed(2),
                    )
                  : null,
              ),
              hoverBackgroundColor: `color-mix(in srgb, ${colorGreenChartDark(
                docStyle,
              )}, ${textColorSecondary} 20%)`,
              hidden: !datasetVisibility.reported,
            },
          ]
        : []),
      {
        type: 'line',
        label: t('phishing.chart.clickRateTrend'),
        borderColor: colorGreenChartLight(docStyle),
        borderWidth: 1,
        borderDash: [10, 5],
        fill: false,
        tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: `color-mix(in srgb, ${colorGreenChartLight(
          docStyle,
        )}, ${textColorSecondary} 20%)`,
        spanGaps: true,
        data: clickRateTrend,
        showLine: true,
        hidden: !datasetVisibility.clickedTrend,
      },
      {
        type: 'line',
        label: t('phishing.chart.submissionRateTrend'),
        borderColor: colorGreenChartMiddle(docStyle),
        borderWidth: 1,
        borderDash: [10, 5],
        fill: false,
        tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: `color-mix(in srgb, ${colorGreenChartMiddle(
          docStyle,
        )}, ${textColorSecondary} 20%)`,
        spanGaps: true,
        data: submissionRateTrend,
        showLine: true,
        hidden: !datasetVisibility.submittedTrend,
      },
      ...(hasReportingEnabled
        ? [
            {
              type: 'line',
              label: t('phishing.chart.reportRateTrend'),
              borderColor: colorGreenChartDark(docStyle),
              borderWidth: 1,
              borderDash: [10, 5],
              fill: false,
              tension: 0,
              pointRadius: 0,
              pointHoverRadius: 5,
              pointHoverBackgroundColor: `color-mix(in srgb, ${colorGreenChartDark(
                docStyle,
              )}, ${textColorSecondary} 20%)`,
              spanGaps: true,
              data: reportRateTrend,
              showLine: true,
              hidden: !datasetVisibility.reportedTrend,
            },
          ]
        : []),
    ];

    const data = {
      labels: datasets?.map((item: any) => item.label),
      datasets: chartDatasets,
    };

    const options = {
      maintainAspectRatio: false,
      aspectRatio: 0.6,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      plugins: {
        tooltip: {
          enabled: false,
          external: (context: any) => createCustomTooltip(context, t, datasets),
        },
        legend: {
          labels: {
            color: textColor,
            usePointStyle: true,
            pointStyle: 'circle',
            boxWidth: 10,
            boxHeight: 10,
            padding: 40,
            font: {
              family: platformFontFamily(docStyle),
            },
            generateLabels: (chart: any) => {
              if (!chart || !chart.data || !chart.data.datasets) {
                return [];
              }

              const baseLabels = [
                {
                  text: t('phishing.chart.clickRate'),
                  fillStyle: datasetVisibility.clicked
                    ? colorGreenChartLight(docStyle)
                    : colorGrayDark(docStyle),
                  strokeStyle: datasetVisibility.clicked
                    ? colorGreenChartLight(docStyle)
                    : colorGrayDark(docStyle),
                  pointStyle: 'circle',
                  lineWidth: 1,
                  hidden: !datasetVisibility.clicked,
                  datasetIndex: 0,
                },
                {
                  text: t('phishing.chart.submissionRate'),
                  fillStyle: datasetVisibility.submitted
                    ? colorGreenChartMiddle(docStyle)
                    : colorGrayDark(docStyle),
                  strokeStyle: datasetVisibility.submitted
                    ? colorGreenChartMiddle(docStyle)
                    : colorGrayDark(docStyle),
                  pointStyle: 'circle',
                  lineWidth: 1,
                  hidden: !datasetVisibility.submitted,
                  datasetIndex: 1,
                },
              ];

              const reportLabels = hasReportingEnabled
                ? [
                    {
                      text: t('phishing.chart.reportRate'),
                      fillStyle: datasetVisibility.reported
                        ? colorGreenChartDark(docStyle)
                        : colorGrayDark(docStyle),
                      strokeStyle: datasetVisibility.reported
                        ? colorGreenChartDark(docStyle)
                        : colorGrayDark(docStyle),
                      pointStyle: 'circle',
                      lineWidth: 1,
                      hidden: !datasetVisibility.reported,
                      datasetIndex: hasReportingEnabled ? 2 : -1,
                    },
                  ]
                : [];

              const trendLabels = [
                {
                  text: t('phishing.chart.clickRateTrend'),
                  fillStyle: 'transparent',
                  strokeStyle: datasetVisibility.clickedTrend
                    ? colorGreenChartLight(docStyle)
                    : colorGrayDark(docStyle),
                  pointStyle: 'circle',
                  lineWidth: 2,
                  lineDash: [3, 1],
                  hidden: !datasetVisibility.clickedTrend,
                  datasetIndex: hasReportingEnabled ? 3 : 2,
                },
                {
                  text: t('phishing.chart.submissionRateTrend'),
                  fillStyle: 'transparent',
                  strokeStyle: datasetVisibility.submittedTrend
                    ? colorGreenChartMiddle(docStyle)
                    : colorGrayDark(docStyle),
                  pointStyle: 'circle',
                  lineWidth: 2,
                  lineDash: [3, 1],
                  hidden: !datasetVisibility.submittedTrend,
                  datasetIndex: hasReportingEnabled ? 4 : 3,
                },
              ];

              const reportTrendLabels = hasReportingEnabled
                ? [
                    {
                      text: t('phishing.chart.reportRateTrend'),
                      fillStyle: 'transparent',
                      strokeStyle: datasetVisibility.reportedTrend
                        ? colorGreenChartDark(docStyle)
                        : colorGrayDark(docStyle),
                      pointStyle: 'circle',
                      lineWidth: 2,
                      lineDash: [3, 1],
                      hidden: !datasetVisibility.reportedTrend,
                      datasetIndex: 5,
                    },
                  ]
                : [];

              const backgroundLabel = {
                text: t('phishing.analytics.periodWithSimulation'),
                fillStyle: datasetVisibility.background
                  ? colorGrayPale(docStyle)
                  : colorGrayDark(docStyle),
                strokeStyle: datasetVisibility.background
                  ? colorGrayPale(docStyle)
                  : colorGrayDark(docStyle),
                pointStyle: 'circle',
                lineWidth: 1,
                hidden: !datasetVisibility.background,
                datasetIndex: -1,
              };

              return [
                ...baseLabels,
                ...reportLabels,
                ...trendLabels,
                ...reportTrendLabels,
                backgroundLabel,
              ];
            },
          },
          position: 'bottom' as const,
          align: 'start' as const,
          onClick: (e: any, legendItem: any, legend: any) => {
            if (!e || !legendItem || !legend || !legend.chart) return;

            if (legendItem.datasetIndex !== undefined) {
              if (legendItem.datasetIndex === -1) {
                handleLegendClick('background');
                return;
              }

              const datasetKeys = hasReportingEnabled
                ? [
                    'clicked',
                    'submitted',
                    'reported',
                    'clickedTrend',
                    'submittedTrend',
                    'reportedTrend',
                  ]
                : ['clicked', 'submitted', 'clickedTrend', 'submittedTrend'];
              const datasetKey = datasetKeys[legendItem.datasetIndex];
              if (datasetKey) {
                handleLegendClick(datasetKey);
              }
            }
          },
          onHover: (e: any, legendItem: any, legend: any) => {
            if (!e || !legend || !legend.chart || !legend.chart.canvas) return;

            const canvas = legend.chart.canvas;
            if (canvas && canvas.style) {
              canvas.style.cursor = legendItem?.text ? 'pointer' : 'default';
            }
          },
        },
        datalabels: {
          display: false,
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
            font: {
              family: platformFontFamily(docStyle),
            },
          },
          grid: {
            display: true,
            color: colorGrayDark(docStyle),
            lineWidth: 1,
            dash: [10, 10],
            drawOnChartArea: true,
          },
          barPercentage: 0.5,
          categoryPercentage: 0.6,
          border: {
            dash: [5, 5],
            display: false,
          },
        },
        y: {
          min: 0,
          max: roundedMax,
          ticks: {
            color: textColorSecondary,
            stepSize: roundedMax <= 20 ? 5 : roundedMax <= 40 ? 10 : 20,
            callback: (value: number) => `${value}%`,
            font: {
              family: platformFontFamily(docStyle),
            },
          },
          grid: {
            display: true,
            color: colorGrayDark(docStyle),
            lineWidth: 1,
            dash: [10, 10],
            drawOnChartArea: true,
          },
          border: {
            dash: [5, 5],
            display: false,
          },
        },
      },
      elements: {
        bar: {
          borderRadius: {
            topLeft: 5,
            topRight: 5,
          },
        },
        line: {
          borderDash: [7, 5],
        },
      },
      onHover: (event: any, activeElements: any[]) => {
        if (!event?.native?.target?.style) return;
        event.native.target.style.cursor =
          activeElements.length > 0 && isMainDashboard ? 'pointer' : 'default';
      },
      onClick: (event: any, activeElements: any[], chart: any) => {
        if (!activeElements || !chart?.canvas) return;

        if (activeElements.length > 0 && isMainDashboard) {
          const tooltipId = `chart-tooltip-${chart.canvas.id || 'default'}`;
          hideTooltipForNavigation(tooltipId);
          navigate(RedirectPaths[RedirectPathsEnum.PHISHING_ANALYTICS]());
        }
      },
    };

    const bgPlugin = {
      id: 'backgroundFill',
      beforeDraw: (chart: any) => {
        if (!datasetVisibility.background) return;

        const ctx = chart.ctx;
        const chartArea = chart.chartArea;

        if (!chartArea || !backgroundData) return;

        const labels = chart.data.labels;
        const categoryWidth = chartArea.width / labels.length;

        ctx.save();
        ctx.fillStyle = colorGrayPale(docStyle) + 'E6';

        backgroundData.forEach((value: any, index: any) => {
          if (value !== null) {
            const x = chartArea.left + index * categoryWidth;
            ctx.fillRect(x, chartArea.top, categoryWidth, chartArea.height);
          }
        });

        ctx.restore();
      },
    };

    setChartData(data);
    setChartOptions(options);
    setBackgroundPlugin(bgPlugin);
    setIsChartDataLoading(false);
  }, [datasets, datasetVisibility]);

  return (
    <ChartContainer id={id}>
      <ChartWrapper>
        {isLoading || isChartDataLoading ? (
          <Skeleton height="100%" width="100%" />
        ) : hasNoData ? (
          <FlexContainer
            height="100%"
            direction="column"
            justify="center"
            align="center"
          >
            <p className="text-center">{t('phishing.analytics.noData')}</p>
          </FlexContainer>
        ) : (
          <StyledBarChart
            type="line"
            data={chartData}
            options={chartOptions}
            plugins={backgroundPlugin ? [backgroundPlugin] : []}
            id={`${id}-line-chart`}
          />
        )}
      </ChartWrapper>
    </ChartContainer>
  );
};
